import React, { useContext, useReducer, useRef } from 'react';
import { func, string } from 'prop-types';
import classNames from 'classnames';
import { Col, Row } from '@thd-olt-component-react/grid';
import { Button } from '@thd-olt-component-react/button';
import { Input } from '@thd-olt-component-react/input';
import { Image } from '@thd-olt-component-react/image';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { DeviceDetection } from '../../../DeviceDetection/DeviceDetection.component';
import { ProductReviewField } from './ProductReviewField';
import { ImageUpload } from './ImageUpload';
import { UserDetails } from './UserDetails';
import { FormFooter } from './FormFooter';
import { ProductRatingDetails } from './ProductRatingDetails';
import * as api from '../api';
import { track } from '../../../../ratings-reviews-analytics';
import { usePresentation } from '../../../../context/PresentationProvider';

const initialState = {
  deviceFingerprint: '',
  displayName: '',
  email: '',
  errorHeading: null,
  errorMessage: null,
  formHeader: null,
  images: [],
  ratings: [],
  recommend: null,
  reviewSubmitted: false,
  reviewText: '',
  reviewTitle: ''
};

function reducer(state, { action, value }) {
  switch (action) {
    case 'deviceFingerprint':
      return { ...state, deviceFingerprint: value };
    case 'displayName':
      return { ...state, displayName: value };
    case 'email':
      return { ...state, email: value };
    case 'errorHeading':
      return { ...state, errorHeading: value };
    case 'errorMessage':
      return { ...state, errorMessage: value };
    case 'formHeader':
      return { ...state, formHeader: value };
    case 'images':
      return { ...state, images: value };
    case 'ratings':
      return { ...state, ratings: value };
    case 'recommend':
      return { ...state, recommend: value };
    case 'reviewSubmitted':
      return { ...state, reviewSubmitted: value };
    case 'reviewText':
      return { ...state, reviewText: value };
    case 'reviewTitle':
      return { ...state, reviewTitle: value };
  }
}

export const WriteAReviewForm = ({ apiKey, closeOverlay, itemId, product, headerText, reviewStatistics }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const formRef = useRef(null);
  const { isServer, hosts } = useContext(ExperienceContext);
  const { useCondensedLayout } = usePresentation();
  const serverHost = (hosts || {}).api;
  const hostName = isServer ?  serverHost : '';
  const {
    deviceFingerprint,
    displayName,
    email,
    errorHeading,
    errorMessage,
    formHeader,
    images,
    ratings,
    recommend,
    reviewSubmitted,
    reviewText,
    reviewTitle
  } = state;
  let formHeaderText = formHeader || headerText;
  const onSubmit = async () => {
    const secondaryRatings = ratings.filter((rating) => rating.name !== 'Overall');
    const overallRating = ratings.find((rating) => rating.name === 'Overall');
    const review = {
      title: reviewTitle,
      text: reviewText,
      userNickName: displayName,
      userEmail: email,
      isRecommended: recommend,
      rating: overallRating ? overallRating.value : '',
      sendEmailAlertWhenPublished: 'yes',
      fp: deviceFingerprint,
      action: 'submit'
    };
    // const bvUserId = WriteAReviewContent.CookieUtils.readBrowserCookie('rm');

    // BV only requires caption and url for review photos
    if (images.length) {
      review.photos = images.map(({ caption, url }) => ({ caption, url }));
    }

    // map secondary ratings for BV :eyeroll:
    secondaryRatings.forEach((rating) => {
        const { key, value } = rating;
        review[key] = value ? value.toString(): null;
      }
    );
    review.secondaryRatings= secondaryRatings? secondaryRatings: [];
    // if (bvUserId) {
    //   review.userId = bvUserId;
    // }

    const response = await api.submitReview(review, itemId, hostName, apiKey, 'submit');
    const { status = 0 } = response || {};
    if (status === 200) {
      dispatch({ action: 'formHeader', value: 'Thanks for sharing your thoughts!' });
      dispatch({ action: 'reviewSubmitted', value: true });
    } else if (status !== 200) {
      formRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
      const simpleResponse = response && typeof response === 'string';
      dispatch({ action: 'errorMessage', value: simpleResponse ? response.replace(/"/g, '') : null });
      dispatch({ action: 'errorHeading', value: simpleResponse ? 'Please complete the following required field(s):' : 'An error occurred. Please try your request again later.' });
    }
    track('event', {
      origin: 'ratings and reviews',
      location: 'writereviewoverlay',
      eventType: 'click',
      value: 'submit'
    });
  };

  const onCloseOverlay = () => {
    closeOverlay()
    dispatch({ action: 'email', value: '' });
    dispatch({ action: 'images', value: [] });
    dispatch({ action: 'ratings', value: [] });
    dispatch({ action: 'recommend', value: null });
    dispatch({ action: 'reviewText', value: '' });
    dispatch({ action: 'formHeader', value: null })
    dispatch({ action: 'displayName', value: '' });
    dispatch({ action: 'reviewTitle', value: '' });
    dispatch({ action: 'errorMessage', value: null });
    dispatch({ action: 'errorHeading', value: null });
    dispatch({ action: 'reviewSubmitted', value: false });
  }
  if (!product) {
    return null;
  }

  const { identifiers, media } = product || {};
  const { brandName = '', productLabel = '' } = identifiers || {};
  let productImage = (media || {}).image;

  const formInputClasses = classNames({
    'write-review-content__no-padding': useCondensedLayout
  });

  return (
    <>
      {!useCondensedLayout &&
        <div className="write-review-content__header">
          <h2>{formHeaderText}</h2>
          <button onClick={onCloseOverlay}>
            <Image src="https://assets.homedepot-static.com/images/v1/close-symbol-orange.svg" alt="Close" />
          </button>
        </div>
      }
      {reviewSubmitted ? (
        <div className="write-review-content__submit__review">
          <span className="write-review-content__submit__thank-you-screen__mobile__content">
            Your review has been submitted successfully. Please note that it may take up to 48 hours to be displayed on homedepot.com
          </span>
          <Button className="write-review-content__submit__close" onClick={onCloseOverlay}>Continue</Button>
        </div>
      ) : (
        <div className="write-review-content" ref={formRef}>
          <DeviceDetection
            onSuccess={(fingerprint) => dispatch({ action: 'deviceFingerprint', value: fingerprint })}
          />
          <ProductRatingDetails
            brandName={brandName}
            className={formInputClasses}
            error={{ heading: errorHeading, message: errorMessage }}
            imageUrl={productImage.url}
            productLabel={productLabel}
            onRecommendationChange={(recommends) => dispatch({ action: 'recommend', value: recommends ? 'yes': 'no' })}
            onRatingChange={(ratings) => dispatch({ action: 'ratings', value: ratings })}
            reviewStatistics={reviewStatistics}
          />
          {/* ReviewTitle */}
          <Row className={formInputClasses}>
            <Col className={formInputClasses}>
              <span className="write-review-content__title">Review Title</span>
              <Input
                placeholder="The most important point"
                onChange={(reviewTitle) => dispatch({ action: 'reviewTitle', value: reviewTitle})}
                value={reviewTitle}
                scrollOnFocus
              />
            </Col>
          </Row>
          {/* ReviewDescription */}
          <ProductReviewField
            formInputClasses={formInputClasses}
            reviewText={reviewText}
            setReviewText={(text) => dispatch({ action: 'reviewText', value: text })}
          />
          {/* UploadImage */}
          <ImageUpload className={formInputClasses} images={images} setImages={(images) => dispatch({ action: 'images', value: images })} />
          <UserDetails
            email={email}
            displayName={displayName}
            onDisplayNameChange={(displayName) => dispatch({ action: 'displayName', value: displayName })}
            onEmailChange={(email) => dispatch({ action: 'email', value: email })}
          />
          <FormFooter onSubmit={onSubmit} />
        </div>
      )}
    </>
  );
};

WriteAReviewForm.displayName = 'WriteAReviewForm';

WriteAReviewForm.propTypes = {
  // Callback to close the overlay the form is shown in
  closeOverlay: func,
  headerText: string,
  itemId: string.isRequired
};

WriteAReviewForm.defaultProps = {
  closeOverlay: null,
  headerText: 'Write A Review'
};
